document.addEventListener("DOMContentLoaded", function () {
	// Function to update the pseudo-element width
	function updatePseudoElementWidth(patternContainer) {
		const ukContainer = patternContainer.querySelector(".uk-container");

		if (ukContainer && patternContainer) {
			const ukContainerOffset = ukContainer.getBoundingClientRect().left;
			const ukContainerPadding = 32; // Padding-left in px
			const adjustedWidth = ukContainerOffset + ukContainerPadding;

			patternContainer.style.setProperty("--pseudo-width", `${adjustedWidth}px`);
		}
	}

	// Function to load/reset and play the Lottie animation for a specific container
	function loadAndPlayLottieAnimation(container, makeVisibleOnLoad = true) {
		const containerId = container.id || container.className.split(" ").join("-");

		if (!container) {
			return;
		}

		const animationPath = container.dataset.animationPath;

		if (!animationPath) {
			if (container.lottieInstance) {
				container.lottieInstance.destroy();
				container.lottieInstance = null;
			}
			container.removeAttribute("style");
			container.innerHTML = "";
			if (makeVisibleOnLoad) container.style.opacity = "1";
			return;
		}

		if (container.lottieInstance) {
			container.lottieInstance.destroy();
			container.lottieInstance = null;
		}

		if (container.lottiePlayTimeout) {
			clearTimeout(container.lottiePlayTimeout);
			container.lottiePlayTimeout = null;
		}

		container.innerHTML = "";

		const animation = window.lottie.loadAnimation({
			container: container,
			renderer: "svg",
			loop: false,
			autoplay: false,
			path: animationPath,
		});
		container.lottieInstance = animation;

		animation.addEventListener("DOMLoaded", () => {
			animation.play();
			if (makeVisibleOnLoad) {
				container.style.opacity = "1";
			}
		});

		const commonErrorHandler = (eventType) => {
			if (container.lottieInstance === animation) {
				container.lottieInstance.destroy();
				container.lottieInstance = null;
				container.innerHTML = "";
				if (makeVisibleOnLoad) container.style.opacity = "1";
			}
		};
		animation.addEventListener("data_failed", () => commonErrorHandler("data_failed"));
		animation.addEventListener("error", () => commonErrorHandler("error"));
	}

	// Function to animate the pseudo-element
	function triggerPseudoElementAnimation(patternContainer) {
		if (patternContainer) {
			// Clear any existing timeouts for this specific patternContainer
			if (patternContainer._animationTimeouts) {
				patternContainer._animationTimeouts.forEach(clearTimeout);
			}
			patternContainer._animationTimeouts = [];

			// Reset classes and force reflow
			patternContainer.classList.remove("animate-expand", "animate-retract");
			void patternContainer.offsetWidth; // Force reflow

			updatePseudoElementWidth(patternContainer);
			patternContainer.classList.add("animate-expand");

			const timeout1 = setTimeout(() => {
				patternContainer.classList.remove("animate-expand");
				patternContainer.classList.add("animate-retract");

				const timeout2 = setTimeout(() => {
					patternContainer.classList.remove("animate-retract");
				}, 1500); // Duration of retract animation
				patternContainer._animationTimeouts.push(timeout2);
			}, 4500); // Time before retract starts
			patternContainer._animationTimeouts.push(timeout1);
		}
	}

	/**
	 * Gets the Swiper autoplay delay defined via the 'swiper' attribute on the container.
	 * This delay is set in Twig using ACF (block.slide_duration).
	 * Returns a fallback value (default 4200ms) if parsing fails.
	 */
	function getSwiperAnimationDelay(container, fallback = 4200) {
		if (!container) return fallback;
		const attr = container.getAttribute("swiper");
		if (!attr) return fallback;

		const parsed = JSON.parse(attr.replace(/&quot;/g, '"').replace(/&amp;/g, "&"));
		let delay = parsed?.autoplay?.delay;

		delay = typeof delay === "string" ? parseInt(delay, 10) : delay;

		if (typeof delay === "number" && !isNaN(delay)) {
			return delay;
		}

		return fallback;
	}

	/**
	 * Manages Wistia videos within the Swiper instance.
	 * Finds players initialized by the standard Wistia script and handles playback.
	 * @param {object} swiperInstance The initialized Swiper instance.
	 */
	function setupWistiaInSwiper(swiperInstance) {
		window._wq = window._wq || [];

		// This function finds a player for a slide and executes an action.
		// It polls because Wistia might take a moment to initialize the player and add an ID to the embed div.
		const findAndControlPlayer = (slide, action, retries = 20) => {
			if (!slide || retries <= 0) {
				return;
			}

			const wistiaEmbed = slide.querySelector(".wistia_embed[id]");

			if (wistiaEmbed && window.Wistia?.api) {
				const player = window.Wistia.api(wistiaEmbed.id);
				if (player) {
					if (action === "play") {
						player.time(0).play();
					} else {
						player.pause();
					}
				} else {
					setTimeout(() => findAndControlPlayer(slide, action, retries - 1), 250);
				}
			} else {
				setTimeout(() => findAndControlPlayer(slide, action, retries - 1), 250);
			}
		};

		// Set up options for all videos found. This runs before the players are created.
		swiperInstance.slides.forEach((slide) => {
			const embed = slide.querySelector('.wistia_embed[class*="wistia_async_"]');
			if (embed) {
				const videoId = embed.className.match(/wistia_async_(\w+)/)?.[1];
				if (videoId) {
					window._wq.push({
						id: videoId,
						options: {
							videoFoam: true,
							playButton: false,
						},
					});
				}
			}
		});

		// Handle the initial slide after a short delay to give Wistia a head start.
		const initialActiveSlide = swiperInstance.slides[swiperInstance.activeIndex];
		findAndControlPlayer(initialActiveSlide, "play");

		swiperInstance.on("slideChangeTransitionStart", function () {
			const previousSlide = this.slides[this.previousIndex];
			findAndControlPlayer(previousSlide, "pause");
		});

		swiperInstance.on("slideChangeTransitionEnd", function () {
			const activeSlide = this.slides[this.activeIndex];
			findAndControlPlayer(activeSlide, "play");
		});
	}

	function initializeHeroAnimationsWithSwiper(swiperInstance, swiperContainerElement) {
		const manageLottieOnSlide = (slideElement, action) => {
			const mainLottieContainers = slideElement.querySelectorAll(".heroMainLottieContainer");
			const heroLottieContainers = slideElement.querySelectorAll(".heroLottieContainer");
			const pattern = slideElement.querySelector(".heroSection__sliderPattern.-desktopSlider");
			const title = slideElement.querySelector(".heroSection__titleSlideDown");

			if (action === "hide") {
				// Hide all lottie containers to prepare for the transition
				const allLottie = [...mainLottieContainers, ...heroLottieContainers];
				allLottie.forEach((lc) => {
					if (lc) {
						lc.style.display = "none";
					}
				});
			} else if (action === "initAndShow") {
				// Always initialize heroMainLottieContainer
				mainLottieContainers.forEach((lc) => {
					if (lc) {
						lc.style.display = "block";
						lc.style.opacity = "0";
						loadAndPlayLottieAnimation(lc, true);
					}
				});

				// Initialize heroLottieContainer and pattern only on desktop
				if (window.matchMedia("(min-width: 1025px)").matches) {
					heroLottieContainers.forEach((lc) => {
						if (lc) {
							lc.style.display = "block";
							lc.style.opacity = "0";
							loadAndPlayLottieAnimation(lc, true);
						}
					});

					if (pattern) {
						triggerPseudoElementAnimation(pattern);
					}
				}

				if (title && title.classList.contains("initial-cursor")) {
					title.classList.remove("slideDownActive");
					void title.offsetWidth;
					const fullDelay = getSwiperAnimationDelay(swiperContainerElement);
					const animationDelay = Math.max(0, fullDelay - 500);
					setTimeout(() => {
						title.classList.add("slideDownActive");
					}, animationDelay);
				}
			}
		};

		swiperInstance.on("slideChangeTransitionStart", function () {
			this.slides.forEach((slide) => manageLottieOnSlide(slide, "hide"));
		});

		swiperInstance.on("slideChangeTransitionEnd", function () {
			const activeSlide = this.slides[this.activeIndex];
			if (activeSlide) {
				manageLottieOnSlide(activeSlide, "initAndShow");
			}
		});

		// Initial setup for the very first active slide
		if (swiperInstance.slides && swiperInstance.slides.length > 0 && swiperInstance.activeIndex !== undefined) {
			const initialActiveSlide = swiperInstance.slides[swiperInstance.activeIndex];
			if (initialActiveSlide) {
				manageLottieOnSlide(initialActiveSlide, "initAndShow");
			}
		} else {
			swiperInstance.once("init", function () {
				const initialActiveSlide = this.slides[this.activeIndex];
				if (initialActiveSlide) {
					manageLottieOnSlide(initialActiveSlide, "initAndShow");
				}
			});
		}

		// Setup Wistia video handling if any videos exist in the slider
		if (swiperContainerElement.querySelector(".wistia_embed")) {
			if (typeof Wistia === "undefined" && !document.querySelector('script[src*="wistia.com"]')) {
				let script = document.createElement("script");
				script.src = "https://fast.wistia.com/assets/external/E-v1.js";
				script.async = true;
				script.onload = () => setupWistiaInSwiper(swiperInstance);
				document.body.appendChild(script);
			} else if (typeof Wistia === "undefined") {
				window.addEventListener("wistia-api-ready", () => setupWistiaInSwiper(swiperInstance), { once: true });
			} else {
				setupWistiaInSwiper(swiperInstance);
			}
		}
	}

	// Set up slide-specific animations
	function trySetupSlideAnimations(attempts = 0) {
		const swiperContainer = document.querySelector(".heroSection__slider");

		if (!swiperContainer) {
			return;
		}

		const slides = swiperContainer.querySelectorAll(".swiper-slide");

		if (slides.length === 1) {
			// Single slide scenario: treat like a static hero animation
			const singleSlide = slides[0];
			const lottieContainer = singleSlide.querySelector(".heroLottieContainer");
			const mainLottieContainer = singleSlide.querySelector(".heroMainLottieContainer");
			const patternContainer = singleSlide.querySelector(".heroSection__sliderPattern.-desktopSlider");

			// Always initialize main lottie
			if (mainLottieContainer) {
				loadAndPlayLottieAnimation(mainLottieContainer);
			}

			// heroLottieContainer and its associated pattern animation are desktop-only.
			if (window.matchMedia("(min-width: 1025px)").matches) {
				if (lottieContainer) {
					loadAndPlayStaticLottie(lottieContainer, patternContainer);
				}

				if (patternContainer) {
					triggerPseudoElementAnimation(patternContainer);
				}
			}
			return;
		}

		// Multi-slide scenario: proceed with Swiper instance initialization
		const swiperInstance = swiperContainer.swiper;

		if (swiperInstance) {
			initializeHeroAnimationsWithSwiper(swiperInstance, swiperContainer);
		} else if (attempts < 20) {
			setTimeout(() => trySetupSlideAnimations(attempts + 1), 500);
		}
	}

	// Function to load, configure, and play Lottie for static hero blocks
	function loadAndPlayStaticLottie(container, patternContainerForLoop) {
		if (!container) return;

		// Clear previous animation if any
		if (container.lottieInstance) {
			container.lottieInstance.destroy();
			container.lottieInstance = null;
		}
		while (container.firstChild) {
			container.removeChild(container.firstChild);
		}

		const animationPath = container.dataset.animationPath;
		if (animationPath) {
			const animation = window.lottie.loadAnimation({
				container: container,
				renderer: "svg",
				loop: true,
				autoplay: false,
				path: animationPath,
			});
			container.lottieInstance = animation;

			animation.addEventListener("DOMLoaded", () => {
				animation.play();
				if (patternContainerForLoop) {
					animation.removeEventListener("loopComplete");
					animation.addEventListener("loopComplete", () => {
						triggerPseudoElementAnimation(patternContainerForLoop);
					});
				}
			});
		}
	}

	// Function to set up animations for static hero blocks
	function setupStaticHeroAnimations() {
		const staticHeroElements = document.querySelectorAll(".heroSection__static.-hasAnimation");

		staticHeroElements.forEach((staticHero) => {
			const lottieContainer = staticHero.querySelector(".heroLottieContainer");
			const mainLottieContainer = staticHero.querySelector(".heroMainLottieContainer");
			const patternContainer = staticHero.querySelector(".heroSection__staticPattern.-hasAnimation");

			// Main Lottie animation runs on all screen sizes.
			if (mainLottieContainer) {
				loadAndPlayLottieAnimation(mainLottieContainer);
			}

			// heroLottieContainer and pattern animation are for desktop only.
			if (window.matchMedia("(min-width: 1025px)").matches) {
				// Lottie animation for static hero (will also trigger pattern animation on loop)
				if (lottieContainer) {
					loadAndPlayStaticLottie(lottieContainer, patternContainer);
				}

				if (patternContainer) {
					triggerPseudoElementAnimation(patternContainer);
				}
			}
		});
	}

	// Initialize Swiper-dependent animations
	trySetupSlideAnimations();

	// Initialize static hero animations (does not depend on Swiper)
	setupStaticHeroAnimations();
	window.addEventListener("resize", () => {
		const patternContainers = document.querySelectorAll(".heroSection__sliderPattern.-desktopSlider, .heroSection__static .heroSection__staticPattern.-hasAnimation");
		patternContainers.forEach(updatePseudoElementWidth);
	});
});
